import { hydrate, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";

import { prepareShellHead } from "@/lib/head";
import { initBrowserMonitoring } from "@/lib/monitoring-browser";
import { initNativeBridge } from "@/lib/native-bridge";
import { setRouter } from "@/lib/nav/router-ref";
import { markHydrating, PRERENDER_STATE_ID, readPrerenderState } from "@/lib/prerender-state";
import { queryClient } from "@/lib/query";
import { router } from "@/router";
// Pretendard 가변 글꼴 — 유니코드 범위 92조각이라 화면에 나온 글자의 조각만 받는다(자체 호스팅, --font-sans 가 첫 이름으로 쓴다).
import "pretendard/dist/web/variable/pretendardvariable-dynamic-subset.css";
import "@/app/globals.css";

initBrowserMonitoring();
setRouter(router);
// 앱 껍데기 안이면 hello·insets·뒤로를 받고 첫 화면 뒤 ready 를 보낸다(브라우저에선 듣기만 한다).
initNativeBridge(router);
// data-shell 기본값을 걷고 고객 화면(/s·/c)이면 index.html 브랜딩도 걷는다 — 운영 Worker 셸(customerShell)과 같은 head · 화면을 옮기면 다시 맞춘다.
const syncShellBrand = prepareShellHead(document.head, window.location.pathname);
router.subscribe("onResolved", ({ toLocation }) => syncShellBrand(toLocation.pathname));

const container = document.getElementById("root")!;
const app = (
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>
);

// 프리렌더·Worker SSR 이 이 주소를 그려 뒀으면 실린 캐시로 같은 화면을 맞춰 붙이고, 아니면 처음부터 그린다.
const state = readPrerenderState(document.getElementById(PRERENDER_STATE_ID), window.location);
if (state) {
  markHydrating(router);
  hydrate(queryClient, state);
  void router.load().then(() => hydrateRoot(container, app));
} else {
  createRoot(container).render(app);
}
